'use client';

import { updateWithToast } from '@/clientOnlyUtils/clientUtils';
import { useState } from 'react';

import { ContactDialNumberPure } from '@poseidon/components/Softphone/ContactDialNumberPure';

import { useSoftphoneLauncher } from '~/components/SoftphoneProvider';
import { api } from '@/utils/api';

export interface ContactDialNumberProps {
  teamId: string;
  /** Public contact id (contact_public_id). */
  contactId: string;
  contactName?: string;
  className?: string;
}

/**
 * Dashboard "Calling from … / Change" caption for the contact detail page — the pre-call sibling of
 * the dialer's in-call affordance in `SoftphoneProvider`. Reads the same `getContactDialInfo` query
 * the dialer uses (same key, so a call, a rotation or a change on either surface refreshes both)
 * and writes through the same `setContactDialNumber` mutation.
 *
 * Mount only when voice calling is enabled for the team and the rep has a number — the query throws
 * FORBIDDEN otherwise. The pure component hides itself when there's nothing to show or pick.
 */
export function ContactDialNumber({ teamId, contactId, contactName, className }: ContactDialNumberProps) {
  const [changeOpen, setChangeOpen] = useState(false);
  // The dialer is an app-level overlay ON TOP of this page and refuses to re-pin during a call —
  // the leg already went out from the old number. Without this the caption behind it would be a
  // back door into the same mutation.
  const { isBusy: dialerOpen } = useSoftphoneLauncher();

  const utils = api.useUtils();
  const dialInfoQuery = api.voiceCall.getContactDialInfo.useQuery({ teamId, contactId });
  const setDialNumberMutation = api.voiceCall.setContactDialNumber.useMutation();
  const clearDialNumberMutation = api.voiceCall.clearContactDialNumber.useMutation();

  // Render nothing until the association is known — a caption that appears and then changes its
  // number under the rep is worse than one that arrives a beat late.
  if (dialInfoQuery.data == null) return null;

  return (
    <ContactDialNumberPure
      contactName={contactName}
      stickyNumber={dialInfoQuery.data.stickyNumber}
      rotationLocked={dialInfoQuery.data.rotationLocked}
      standInNumber={dialInfoQuery.data.standInNumber}
      bucketNumbers={dialInfoQuery.data.bucketNumbers}
      history={dialInfoQuery.data.history}
      numberLimits={dialInfoQuery.data.numberLimits}
      changeDisabledReason={dialerOpen ? 'Finish the call to change the number' : undefined}
      changeOpen={changeOpen}
      saving={setDialNumberMutation.isPending}
      clearing={clearDialNumberMutation.isPending}
      onOpenChange={() => setChangeOpen(true)}
      onCloseChange={() => setChangeOpen(false)}
      onConfirmChange={async (phoneNumber) => {
        await updateWithToast({
          loading: 'Updating calling number…',
          success: 'Calling number updated',
          mutationFnAsync: () => setDialNumberMutation.mutateAsync({ teamId, contactId, phoneNumber }),
          // Only on success (updateWithToast swallows the error): a failed pin leaves the popup
          // open so the rep can retry, instead of silently keeping the old number.
          onAfterSuccess: () => {
            void utils.voiceCall.getContactDialInfo.invalidate({ teamId, contactId });
            setChangeOpen(false);
          },
        });
      }}
      onClear={async () => {
        await updateWithToast({
          loading: 'Clearing calling number…',
          success: 'Calls to this contact will rotate again',
          mutationFnAsync: () => clearDialNumberMutation.mutateAsync({ teamId, contactId }),
          onAfterSuccess: () => {
            void utils.voiceCall.getContactDialInfo.invalidate({ teamId, contactId });
            setChangeOpen(false);
          },
        });
      }}
      className={className}
    />
  );
}
